// Piano-roll gesture math — the pure half of MIDI note editing.
//
// Notes have no ids on the wire, so a gesture can't hold references across a
// commit: the room canonicalises (sorts) the list and hands back a reordered
// array. Everything here keys a note by its VALUE instead (`noteKey`), which
// survives the round trip — and every transform returns a whole new list,
// because that is what `setClipNotes` proposes and what the room stores.

import type { WireNote } from "@shared/midi/notes.js";
import { nearestInScale } from "@web-shared/lib/scales";

/** Shortest note a gesture can produce, in beats (a 32nd at 4/4). */
export const MIN_NOTE_BEATS = 0.125;

const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

/** Ableton's spelling: MIDI 60 is C3, so octave = floor(pitch/12) − 2. */
export function pitchName(pitch: number): string {
  return `${NAMES[((pitch % 12) + 12) % 12]}${Math.floor(pitch / 12) - 2}`;
}

export function isBlackKey(pitch: number): boolean {
  return [1, 3, 6, 8, 10].includes(((pitch % 12) + 12) % 12);
}

/**
 * A note's identity for selection and drags. Pitch + placement is enough:
 * two notes that collide on all three are audibly one note, and acting on
 * both at once is the least surprising reading of a click on either.
 */
export function noteKey(n: WireNote): string {
  return `${n.pitch}:${n.startTime}:${n.duration}`;
}

/**
 * Where to scroll the roll on open: the middle of the content, or middle C
 * for an empty clip.
 */
export function centerPitch(notes: readonly WireNote[]): number {
  if (notes.length === 0) return 60;
  let low = 127;
  let high = 0;
  for (const n of notes) {
    low = Math.min(low, n.pitch);
    high = Math.max(high, n.pitch);
  }
  return Math.round((low + high) / 2);
}

/**
 * Live's same-pitch overlap rule for notes being placed (drawn, moved or
 * stretched) over `others`: an other note that STARTS inside a placed note
 * is overwritten (removed); one that starts before a placed note and runs
 * into it is shortened to end where the placed note begins. Only `others`
 * change — the placed notes are never cut, and placed notes overlapping one
 * another are left as they are. Order of the survivors is kept.
 */
export function resolveSamePitchOverlaps(placed: readonly WireNote[], others: readonly WireNote[]): WireNote[] {
  const out: WireNote[] = [];
  for (const n of others) {
    const r = resolveOne(n, placed);
    if (r) out.push(r);
  }
  return out;
}

/** One other note against the placed ones: itself, shortened, or null when overwritten. */
function resolveOne(n: WireNote, placed: readonly WireNote[]): WireNote | null {
  let end = n.startTime + n.duration;
  for (const p of placed) {
    if (p.pitch !== n.pitch) continue;
    const pEnd = p.startTime + p.duration;
    if (n.startTime >= p.startTime - 1e-9 && n.startTime < pEnd - 1e-9) return null;
    if (n.startTime < p.startTime - 1e-9 && end > p.startTime + 1e-9) end = p.startTime;
  }
  const duration = end - n.startTime;
  return duration === n.duration ? n : { ...n, duration: r4(duration) };
}

/**
 * Move or resize the keyed notes as a group.
 *
 * The delta is clamped against the GROUP's extremes before applying, so a
 * chord dragged into an edge stops whole instead of smearing — per-note
 * clamping would deform exactly the shapes people drag together. Times are
 * clip-relative beats; nothing may start before 0 or at/after the clip's end.
 * A note already hanging past the end (Live keeps those) never blocks the
 * gesture: the clamp ceiling floors at "no movement", not at a rejection.
 *
 * A resize floors each note at MIN_NOTE_BEATS or its own length, whichever
 * is shorter: a 1/64 already below the floor may shrink no further, but the
 * floor never lengthens it. Where the moved/stretched notes land on other
 * notes of the same pitch, `resolveSamePitchOverlaps` applies.
 */
export function dragNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  mode: "move" | "end",
  deltaBeats: number,
  deltaPitch: number,
  clipDuration: number,
): WireNote[] {
  return dragNotesPlaced(notes, keys, mode, deltaBeats, deltaPitch, clipDuration).notes;
}

/** `dragNotes`, also handing back the moved notes as placed — what the
 * caller reselects, since overwritten neighbours make positions shift. */
export function dragNotesPlaced(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  mode: "move" | "end",
  deltaBeats: number,
  deltaPitch: number,
  clipDuration: number,
): { notes: WireNote[]; placed: WireNote[] } {
  const picked = notes.filter((n) => keys.has(noteKey(n)));
  if (picked.length === 0) return { notes: [...notes], placed: [] };

  let transform: (n: WireNote) => WireNote;
  if (mode === "end") {
    transform = (n) => {
      const floor = Math.min(n.duration, MIN_NOTE_BEATS);
      const wanted = Math.max(floor, n.duration + deltaBeats);
      return { ...n, duration: Math.min(wanted, Math.max(MIN_NOTE_BEATS, clipDuration - n.startTime)) };
    };
  } else {
    const minStart = Math.min(...picked.map((n) => n.startTime));
    const maxEnd = Math.max(...picked.map((n) => n.startTime + n.duration));
    const lo = -minStart;
    // Ceiling floors at 0, not at lo: a group already hanging past the end must
    // hold still on a rightward drag, not snap leftward to "fit".
    const db = Math.min(Math.max(deltaBeats, lo), Math.max(0, clipDuration - maxEnd));

    const minPitch = Math.min(...picked.map((n) => n.pitch));
    const maxPitch = Math.max(...picked.map((n) => n.pitch));
    const dp = Math.min(Math.max(deltaPitch, -minPitch), 127 - maxPitch);
    transform = (n) => ({ ...n, startTime: n.startTime + db, pitch: n.pitch + dp });
  }

  const placed = picked.map(transform);
  // Every note keeps its slot in the list; an overwritten neighbour drops out.
  const out: WireNote[] = [];
  let i = 0;
  for (const n of notes) {
    if (keys.has(noteKey(n))) out.push(placed[i++]!);
    else {
      const r = resolveOne(n, placed);
      if (r) out.push(r);
    }
  }
  return { notes: out, placed };
}

/**
 * A double-click's new note: snapped down to the grid, one grid cell long,
 * trimmed to fit the clip. Returns the note too so the caller can select it.
 * Same-pitch neighbours it lands on are overwritten or shortened, as in Live.
 */
export function addNoteAt(
  notes: readonly WireNote[],
  pitch: number,
  atBeat: number,
  grid: number,
  clipDuration: number,
): { notes: WireNote[]; added: WireNote } {
  const start = Math.min(
    Math.max(0, Math.floor(atBeat / grid) * grid),
    Math.max(0, clipDuration - MIN_NOTE_BEATS),
  );
  const added: WireNote = {
    pitch: Math.min(Math.max(0, Math.round(pitch)), 127),
    startTime: start,
    duration: Math.max(MIN_NOTE_BEATS, Math.min(grid, clipDuration - start)),
    velocity: 100,
  };
  return { notes: [...resolveSamePitchOverlaps([added], notes), added], added };
}

export function deleteNotes(notes: readonly WireNote[], keys: ReadonlySet<string>): WireNote[] {
  return notes.filter((n) => !keys.has(noteKey(n)));
}

/** Velocity from a drag on the velocity lane. Floor of 1: a silent note that
 * still occupies its slot is a trap nobody sets on purpose — that's `muted`. */
export function setNoteVelocity(
  notes: readonly WireNote[],
  key: string,
  velocity: number,
): WireNote[] {
  const v = Math.min(Math.max(1, Math.round(velocity)), 127);
  return notes.map((n) => (noteKey(n) === key ? { ...n, velocity: v } : n));
}

/**
 * Snap the keyed notes' starts to `grid` (all notes when `keys` is empty),
 * clamped inside the clip. Durations are untouched — Live's default
 * quantize moves note starts only. Returns the same list when nothing
 * moved, so the caller can skip a no-op proposal.
 */
export function quantizeNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  grid: number,
  clipDuration: number,
): WireNote[] {
  if (!(grid > 0)) return [...notes];
  const all = keys.size === 0;
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const snapped = Math.min(
      Math.max(0, Math.round(n.startTime / grid) * grid),
      Math.max(0, clipDuration - MIN_NOTE_BEATS),
    );
    return snapped === n.startTime ? n : { ...n, startTime: snapped };
  });
}

/** Transpose the keyed notes by `semitones`, the group clamped to 0–127 as
 * a whole so a chord at the top stops whole rather than folding. */
export function transposeNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  semitones: number,
  clipDuration: number,
): WireNote[] {
  return dragNotes(notes, keys, "move", 0, semitones, clipDuration);
}

/** Keys of the notes a rubber band covers: any overlap in time, pitch inclusive. */
export function notesWithin(
  notes: readonly WireNote[],
  fromBeat: number,
  toBeat: number,
  fromPitch: number,
  toPitch: number,
): Set<string> {
  const b0 = Math.min(fromBeat, toBeat);
  const b1 = Math.max(fromBeat, toBeat);
  const p0 = Math.min(fromPitch, toPitch);
  const p1 = Math.max(fromPitch, toPitch);
  const out = new Set<string>();
  for (const n of notes) {
    if (n.pitch < p0 || n.pitch > p1) continue;
    if (n.startTime < b1 && n.startTime + n.duration > b0) out.add(noteKey(n));
  }
  return out;
}

/** Flip `muted` on the keyed notes. A muted note stays on the wire (so
 * un-muting round-trips) and never sounds. */
export function toggleNotesMuted(notes: readonly WireNote[], keys: ReadonlySet<string>): WireNote[] {
  return notes.map((n) => {
    if (!keys.has(noteKey(n))) return n;
    const { muted: _m, ...rest } = n;
    return n.muted ? rest : { ...rest, muted: true };
  });
}

/**
 * Live's ⌘D in the MIDI editor: copies of the keyed notes placed right after
 * the selection's span (its extent rounded up to the grid, so a phrase
 * repeats on the beat), kept inside the clip. Returns the copies too so the
 * caller can select them. No copies when the span doesn't fit.
 */
export function duplicateNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  grid: number,
  clipDuration: number,
): { notes: WireNote[]; added: WireNote[] } {
  const picked = notes.filter((n) => keys.has(noteKey(n)));
  if (picked.length === 0) return { notes: [...notes], added: [] };
  const from = Math.min(...picked.map((n) => n.startTime));
  const to = Math.max(...picked.map((n) => n.startTime + n.duration));
  const span = grid > 0 ? Math.ceil((to - from) / grid - 1e-9) * grid : to - from;
  if (!(span > 0)) return { notes: [...notes], added: [] };
  const added = picked
    .map((n) => ({ ...n, startTime: n.startTime + span }))
    .filter((n) => n.startTime < clipDuration - 1e-9);
  return { notes: [...notes, ...added], added };
}

/** Velocity for every keyed note at once — a drag on one selected bar sets
 * the whole selection, as Live does. Same floor as `setNoteVelocity`. */
export function setNotesVelocity(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  velocity: number,
): WireNote[] {
  const v = Math.min(Math.max(1, Math.round(velocity)), 127);
  return notes.map((n) => (keys.has(noteKey(n)) ? { ...n, velocity: v } : n));
}

// ---- Computer-keyboard input ------------------------------------------------
//
// Live's own layout: the home row is the white keys from C, the row above the
// black keys, Z/X shift the octave. Pure lookups, so the roll's key handler
// stays a switch on results rather than a table of its own.

/** Keyboard key (lower-case) → semitone above the octave's C. */
const KEY_SEMITONES: Record<string, number> = {
  a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11, k: 12, o: 13, l: 14, p: 15, ";": 16,
};

/** The pitch a key plays at an octave (Ableton's spelling: octave 3 ⇒ C3 = 60),
 * or null for a key that isn't a note. */
export function pitchForKey(key: string, octave: number): number | null {
  const semitone = KEY_SEMITONES[key.toLowerCase()];
  if (semitone === undefined) return null;
  const pitch = (octave + 2) * 12 + semitone;
  return pitch >= 0 && pitch <= 127 ? pitch : null;
}

/** Lowest and highest octave the keyboard can be shifted to (C-2 … C8). */
export const OCTAVE_MIN = -2;
export const OCTAVE_MAX = 8;

/**
 * Step input: put a note of `grid` beats at the cursor and advance the
 * cursor by one grid step. A chord is several calls at the SAME cursor —
 * the caller advances only when the last key of the chord lifts — so this
 * never moves the cursor itself; it reports where the next step is. A
 * cursor at or past the clip's end wraps to 0, the way Live's step cursor
 * stays inside the clip.
 */
export function stepInsert(
  notes: readonly WireNote[],
  pitch: number,
  cursor: number,
  grid: number,
  clipDuration: number,
  velocity = 100,
): { notes: WireNote[]; note: WireNote; nextCursor: number } {
  const at = cursor >= clipDuration - 1e-9 || cursor < 0 ? 0 : cursor;
  const note: WireNote = {
    pitch: Math.min(Math.max(0, Math.round(pitch)), 127),
    startTime: r4(at),
    duration: Math.max(MIN_NOTE_BEATS, Math.min(grid, clipDuration - at)),
    velocity,
  };
  // A note already exactly there at that pitch is the same note: don't stack.
  const dup = notes.some((n) => n.pitch === note.pitch && Math.abs(n.startTime - note.startTime) < 1e-9);
  return { notes: dup ? [...notes] : [...notes, note], note, nextCursor: r4(at + grid) };
}

/**
 * A note played live against the transport: held from `downBeat` to
 * `upBeat` (clip-relative), start snapped to the grid, length at least one
 * grid step — a tap is a grid note, not a sliver — and trimmed to the clip.
 * Null when the press began after the clip's end.
 */
export function playedNote(
  pitch: number,
  downBeat: number,
  upBeat: number,
  grid: number,
  clipDuration: number,
  velocity = 100,
  /** The playhead loops round the clip (a launched slot clip): a key still
   * down when it wraps was held across the end, not lifted before it went
   * down. The note is cut at the clip's end, as Live cuts it. */
  looped = false,
): WireNote | null {
  const start = Math.round(downBeat / grid) * grid;
  if (start < 0 || start >= clipDuration - 1e-9) return null;
  const up = looped && upBeat < downBeat ? upBeat + clipDuration : upBeat;
  const held = Math.max(grid, Math.round((up - downBeat) / grid) * grid);
  return {
    pitch: Math.min(Math.max(0, Math.round(pitch)), 127),
    startTime: r4(start),
    duration: r4(Math.max(MIN_NOTE_BEATS, Math.min(held, clipDuration - start))),
    velocity,
  };
}

function r4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}

// ---- Musical transforms -----------------------------------------------------

/**
 * Legato: each keyed note (all notes when `keys` is empty) is stretched to
 * the start of the next note that begins after it on the SAME pitch class
 * set — i.e. the next note-start in the selection, any pitch — so a phrase
 * of overlapping or gapped notes becomes a continuous line. The last note
 * keeps its length. A note already reaching further is left alone.
 */
export function legatoNotes(notes: readonly WireNote[], keys: ReadonlySet<string>): WireNote[] {
  const all = keys.size === 0;
  const picked = notes.filter((n) => all || keys.has(noteKey(n)));
  const starts = [...new Set(picked.map((n) => n.startTime))].sort((a, b) => a - b);
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const next = starts.find((s) => s > n.startTime + 1e-9);
    if (next === undefined) return n;
    const duration = Math.max(MIN_NOTE_BEATS, next - n.startTime);
    if (n.duration >= duration - 1e-9) return n; // already reaches (or overlaps) — a stretch, not a trim
    return { ...n, duration };
  });
}

/**
 * A velocity ramp across the keyed notes in start order: the first gets
 * `from`, the last `to`, the rest interpolated by position in time (not by
 * index, so a chord shares one value). One note takes `to`.
 */
export function rampVelocity(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  from: number,
  to: number,
): WireNote[] {
  const picked = notes.filter((n) => keys.has(noteKey(n)));
  if (picked.length === 0) return [...notes];
  const first = Math.min(...picked.map((n) => n.startTime));
  const last = Math.max(...picked.map((n) => n.startTime));
  const span = last - first;
  const clamp = (v: number) => Math.min(127, Math.max(1, Math.round(v)));
  return notes.map((n) => {
    if (!keys.has(noteKey(n))) return n;
    const t = span > 0 ? (n.startTime - first) / span : 1;
    return { ...n, velocity: clamp(from + (to - from) * t) };
  });
}

/**
 * Humanize: nudge the keyed notes' starts by up to ±`timingBeats` and their
 * velocities by up to ±`velocityRange`, from the caller's random source
 * (0..1) so a test can be exact. Starts stay inside the clip; velocities
 * inside 1–127; lengths untouched.
 */
export function humanizeNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  timingBeats: number,
  velocityRange: number,
  clipDuration: number,
  random: () => number = Math.random,
): WireNote[] {
  const all = keys.size === 0;
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const dt = (random() * 2 - 1) * timingBeats;
    const dv = (random() * 2 - 1) * velocityRange;
    const startTime = Math.min(Math.max(0, n.startTime + dt), Math.max(0, clipDuration - MIN_NOTE_BEATS));
    const velocity = Math.min(127, Math.max(1, Math.round(n.velocity + dv)));
    return { ...n, startTime: Math.round(startTime * 1e4) / 1e4, velocity };
  });
}

/**
 * Fold the keyed notes (all when `keys` is empty) onto a scale: each
 * out-of-scale pitch moves to the nearest scale degree (ties down —
 * lib/scales.ts). Timing and velocity untouched; an already-in-scale list
 * comes back unchanged so the caller can skip the proposal.
 */
export function foldToScale(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  root: number,
  intervals: readonly number[],
): WireNote[] {
  const all = keys.size === 0;
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const pitch = nearestInScale(n.pitch, root, intervals);
    return pitch === n.pitch ? n : { ...n, pitch };
  });
}

/**
 * Chop: each keyed note (all when `keys` is empty) becomes a run of
 * `grid`-long notes over its span, the last one taking the remainder when
 * that is at least a 32nd — Live 12's Chop tool, the rhythmic half of a
 * held note. Pitch and velocity carry into every piece.
 */
export function chopNotes(notes: readonly WireNote[], keys: ReadonlySet<string>, grid: number): WireNote[] {
  if (!(grid > 0)) return [...notes];
  const all = keys.size === 0;
  const out: WireNote[] = [];
  for (const n of notes) {
    if ((!all && !keys.has(noteKey(n))) || n.duration <= grid + 1e-9) {
      out.push(n);
      continue;
    }
    let at = 0;
    while (at < n.duration - 1e-9) {
      const remaining = n.duration - at;
      const length = remaining >= grid + MIN_NOTE_BEATS - 1e-9 ? grid : remaining;
      out.push({ ...n, startTime: r4(n.startTime + at), duration: r4(length) });
      at += length;
    }
  }
  return out;
}

/**
 * Arpeggiate: notes in the selection that start together (a chord) are
 * replaced by a run of `rate`-long single notes cycling up through the
 * chord's pitches for the chord's span. Notes that stand alone are left
 * as they are. The pattern is Live's "Up".
 */
export function arpeggiateNotes(notes: readonly WireNote[], keys: ReadonlySet<string>, rate: number): WireNote[] {
  if (!(rate > 0)) return [...notes];
  const all = keys.size === 0;
  const picked = notes.filter((n) => all || keys.has(noteKey(n)));
  const groups = new Map<number, WireNote[]>();
  for (const n of picked) {
    const key = r4(n.startTime);
    groups.set(key, [...(groups.get(key) ?? []), n]);
  }
  const replaced = new Set<WireNote>();
  const added: WireNote[] = [];
  for (const chord of groups.values()) {
    if (chord.length < 2) continue;
    const pitches = [...new Set(chord.map((n) => n.pitch))].sort((a, b) => a - b);
    const start = chord[0]!.startTime;
    const end = Math.max(...chord.map((n) => n.startTime + n.duration));
    const velocity = Math.round(chord.reduce((s, n) => s + n.velocity, 0) / chord.length);
    let i = 0;
    for (let at = start; at < end - 1e-9; at += rate, i++) {
      const length = Math.max(MIN_NOTE_BEATS, Math.min(rate, end - at));
      added.push({ pitch: pitches[i % pitches.length]!, startTime: r4(at), duration: r4(length), velocity });
    }
    for (const n of chord) replaced.add(n);
  }
  if (added.length === 0) return [...notes];
  return [...notes.filter((n) => !replaced.has(n)), ...added];
}

/**
 * Reverse (retrograde): the keyed notes are mirrored in time within their
 * own span, so the last note plays first. Pitches and lengths stay; a
 * note's new start is where its END used to be, measured from the other
 * side. Nothing outside the selection moves.
 */
export function reverseNotes(notes: readonly WireNote[], keys: ReadonlySet<string>): WireNote[] {
  const all = keys.size === 0;
  const picked = notes.filter((n) => all || keys.has(noteKey(n)));
  if (picked.length === 0) return [...notes];
  const from = Math.min(...picked.map((n) => n.startTime));
  const to = Math.max(...picked.map((n) => n.startTime + n.duration));
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const startTime = r4(from + to - (n.startTime + n.duration));
    return startTime === n.startTime ? n : { ...n, startTime: Math.max(0, startTime) };
  });
}

// ---- Chance and velocity deviation (Live 11's per-note lanes) ----------------
//
// Both fields are on the wire already (`WireNote.probability`, 0..1, default
// 1; `WireNote.velocityDeviation`, -127..127, default 0 — canonical form omits
// a default). The roll edits them in their own lanes; `resolveNote` below is
// what the preview plays: a note with chance p sounds p of the time, and a
// deviation d adds a random amount between 0 and d to its velocity (negative
// d subtracts), exactly Live's reading.

/** Chance for every keyed note, 0..1, rounded to a percent. */
export function setNotesProbability(notes: readonly WireNote[], keys: ReadonlySet<string>, probability: number): WireNote[] {
  const p = Math.round(Math.min(Math.max(0, probability), 1) * 100) / 100;
  return notes.map((n) => {
    if (!keys.has(noteKey(n))) return n;
    const { probability: _drop, ...rest } = n;
    return p === 1 ? rest : { ...rest, probability: p };
  });
}

/** Velocity deviation for every keyed note, -127..127, whole numbers. */
export function setNotesDeviation(notes: readonly WireNote[], keys: ReadonlySet<string>, deviation: number): WireNote[] {
  const d = Math.round(Math.min(Math.max(-127, deviation), 127));
  return notes.map((n) => {
    if (!keys.has(noteKey(n))) return n;
    const { velocityDeviation: _drop, ...rest } = n;
    return d === 0 ? rest : { ...rest, velocityDeviation: d };
  });
}

/**
 * What a note sounds as on one pass: null when its chance didn't land, else
 * the note with its velocity spread by the deviation, clamped to 1..127.
 * `rng` yields 0..1; the default is Math.random, a seeded one makes the
 * outcome testable.
 */
export function resolveNote(n: WireNote, rng: () => number = Math.random): WireNote | null {
  const p = n.probability ?? 1;
  if (p < 1 && rng() >= p) return null;
  const d = n.velocityDeviation ?? 0;
  if (d === 0) return n;
  const velocity = Math.min(127, Math.max(1, Math.round(n.velocity + rng() * d)));
  return { ...n, velocity };
}

// ---- ×2 / ÷2 -----------------------------------------------------------------

/**
 * Live's "×2": the clip doubles and its notes repeat in the second half.
 * "÷2" halves it, keeping the notes that start in the first half (one that
 * crosses the new end is cut at it). Returns the notes for the new length.
 */
export function scaleLoopNotes(notes: readonly WireNote[], duration: number, factor: 2 | 0.5): WireNote[] {
  if (factor === 2) {
    return [...notes, ...notes.map((n) => ({ ...n, startTime: n.startTime + duration }))];
  }
  const half = duration / 2;
  const out: WireNote[] = [];
  for (const n of notes) {
    if (n.startTime >= half) continue;
    const cut = Math.min(n.duration, half - n.startTime);
    if (cut <= 0) continue;
    out.push(cut === n.duration ? n : { ...n, duration: cut });
  }
  return out;
}

// ---- FL Studio's gestures and tools --------------------------------------
//
// What an FL Studio user reaches for that Live's roll has no word for, on
// Live's note model: a clone drag, a clipboard, Paint, Glue, Strum, Flam,
// Flip, Articulate (note lengths scaled), Scale levels (velocities scaled)
// and the chord Stamp. All pure over wire notes, like everything above.

/**
 * A clone drag (FL's ⇧-drag, Live's ⌥-drag): the keyed notes stay where
 * they are and COPIES land at the delta, clamped as a group like `dragNotes`.
 * Everything already there — the originals included — yields to the copies
 * on their pitch, as placed notes always do. Hands back the copies so the
 * caller can select them.
 */
export function copyDragNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  deltaBeats: number,
  deltaPitch: number,
  clipDuration: number,
): { notes: WireNote[]; placed: WireNote[] } {
  const picked = notes.filter((n) => keys.has(noteKey(n)));
  if (picked.length === 0) return { notes: [...notes], placed: [] };
  const minStart = Math.min(...picked.map((n) => n.startTime));
  const maxEnd = Math.max(...picked.map((n) => n.startTime + n.duration));
  const db = Math.min(Math.max(deltaBeats, -minStart), Math.max(0, clipDuration - maxEnd));
  const minPitch = Math.min(...picked.map((n) => n.pitch));
  const maxPitch = Math.max(...picked.map((n) => n.pitch));
  const dp = Math.min(Math.max(deltaPitch, -minPitch), 127 - maxPitch);
  if (db === 0 && dp === 0) return { notes: [...notes], placed: [] };
  const placed = picked.map((n) => ({ ...n, startTime: r4(n.startTime + db), pitch: n.pitch + dp }));
  return { notes: [...resolveSamePitchOverlaps(placed, notes), ...placed], placed };
}

/**
 * Paste: the copied notes land with their earliest start at `atBeat`,
 * voicing and spacing kept; whatever would start at or past the clip's end
 * is dropped, a straddler is cut at the end, and same-pitch neighbours
 * yield as to any placed note. Hands back what landed, for selection.
 */
export function pasteNotes(
  notes: readonly WireNote[],
  clipboard: readonly WireNote[],
  atBeat: number,
  clipDuration: number,
): { notes: WireNote[]; added: WireNote[] } {
  if (clipboard.length === 0) return { notes: [...notes], added: [] };
  const from = Math.min(...clipboard.map((n) => n.startTime));
  const at = Math.max(0, atBeat);
  const added: WireNote[] = [];
  for (const n of clipboard) {
    const startTime = r4(n.startTime - from + at);
    if (startTime >= clipDuration - 1e-9) continue;
    const duration = r4(Math.max(MIN_NOTE_BEATS, Math.min(n.duration, clipDuration - startTime)));
    added.push({ ...n, startTime, duration });
  }
  if (added.length === 0) return { notes: [...notes], added: [] };
  return { notes: [...resolveSamePitchOverlaps(added, notes), ...added], added };
}

/**
 * FL's Glue (⌘G): keyed notes (all when `keys` is empty) that touch or
 * overlap on the same pitch become one note from the first's start to the
 * last's end, carrying the first's velocity and per-note settings. Notes
 * with a gap between them stay apart. The same list comes back when
 * nothing joins, so the caller can skip the proposal.
 */
export function glueNotes(notes: readonly WireNote[], keys: ReadonlySet<string>): WireNote[] {
  const all = keys.size === 0;
  const byPitch = new Map<number, WireNote[]>();
  for (const n of notes) {
    if (!all && !keys.has(noteKey(n))) continue;
    byPitch.set(n.pitch, [...(byPitch.get(n.pitch) ?? []), n]);
  }
  const replaced = new Map<WireNote, WireNote | null>();
  for (const run of byPitch.values()) {
    const sorted = [...run].sort((a, b) => a.startTime - b.startTime);
    let head = sorted[0]!;
    let end = head.startTime + head.duration;
    let members: WireNote[] = [head];
    const flush = () => {
      if (members.length < 2) return;
      replaced.set(head, { ...head, duration: r4(end - head.startTime) });
      for (const m of members.slice(1)) replaced.set(m, null);
    };
    for (const n of sorted.slice(1)) {
      if (n.startTime <= end + 1e-9) {
        end = Math.max(end, n.startTime + n.duration);
        members.push(n);
      } else {
        flush();
        head = n;
        end = n.startTime + n.duration;
        members = [n];
      }
    }
    flush();
  }
  if (replaced.size === 0) return [...notes];
  const out: WireNote[] = [];
  for (const n of notes) {
    if (!replaced.has(n)) out.push(n);
    else {
      const r = replaced.get(n);
      if (r) out.push(r);
    }
  }
  return out;
}

/**
 * FL's Flip (vertical): the keyed notes (all when `keys` is empty) are
 * mirrored in pitch within their own range — the top note becomes the
 * bottom one. Timing untouched; a single pitch comes back unchanged.
 */
export function flipNotes(notes: readonly WireNote[], keys: ReadonlySet<string>): WireNote[] {
  const all = keys.size === 0;
  const picked = notes.filter((n) => all || keys.has(noteKey(n)));
  if (picked.length === 0) return [...notes];
  const lo = Math.min(...picked.map((n) => n.pitch));
  const hi = Math.max(...picked.map((n) => n.pitch));
  if (lo === hi) return [...notes];
  return notes.map((n) => (all || keys.has(noteKey(n)) ? { ...n, pitch: lo + hi - n.pitch } : n));
}

/**
 * FL's Scale levels, the velocity half: every keyed note's velocity
 * (all when `keys` is empty) is multiplied by `factor`, so a phrase gets
 * louder or softer and keeps its dynamics — unlike the lane, which sets
 * one value on all. Clamped 1..127.
 */
export function scaleVelocity(notes: readonly WireNote[], keys: ReadonlySet<string>, factor: number): WireNote[] {
  const all = keys.size === 0;
  return notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const velocity = Math.min(127, Math.max(1, Math.round(n.velocity * factor)));
    return velocity === n.velocity ? n : { ...n, velocity };
  });
}

/**
 * FL's Articulate, the plain half: every keyed note's length (all when
 * `keys` is empty) is multiplied by `factor` — ½ is staccato, 2 stretches —
 * floored at a 32nd and cut at the clip's end. Starts never move. Where a
 * stretched note runs into the next on its pitch, that one yields.
 */
export function scaleLength(notes: readonly WireNote[], keys: ReadonlySet<string>, factor: number, clipDuration: number): WireNote[] {
  if (!(factor > 0)) return [...notes];
  const all = keys.size === 0;
  const stretched: WireNote[] = [];
  const mapped = notes.map((n) => {
    if (!all && !keys.has(noteKey(n))) return n;
    const duration = r4(Math.max(MIN_NOTE_BEATS, Math.min(n.duration * factor, Math.max(MIN_NOTE_BEATS, clipDuration - n.startTime))));
    if (duration === n.duration) return n;
    const next = { ...n, duration };
    if (duration > n.duration) stretched.push(next);
    return next;
  });
  if (stretched.length === 0) return mapped;
  // A grown note overwrites or shortens only what it did not grow with.
  const grown = new Set(stretched);
  const out: WireNote[] = [];
  for (const n of mapped) {
    if (grown.has(n)) out.push(n);
    else {
      const r = resolveOne(n, stretched);
      if (r) out.push(r);
    }
  }
  return out;
}

/**
 * FL's Strum: each chord in the selection (notes starting together, two
 * or more) is spread so its notes start `offsetBeats` apart — up from the
 * lowest, or down from the highest — every note still ending where the
 * chord did, as a strummed guitar sounds. A note that would be pushed past
 * its own end keeps a 32nd. Lone notes are left alone.
 */
export function strumNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  offsetBeats: number,
  direction: "up" | "down" = "up",
): WireNote[] {
  if (!(offsetBeats > 0)) return [...notes];
  const all = keys.size === 0;
  const groups = new Map<number, WireNote[]>();
  for (const n of notes) {
    if (!all && !keys.has(noteKey(n))) continue;
    const key = r4(n.startTime);
    groups.set(key, [...(groups.get(key) ?? []), n]);
  }
  const moved = new Map<WireNote, WireNote>();
  for (const chord of groups.values()) {
    if (chord.length < 2) continue;
    const order = [...chord].sort((a, b) => (direction === "up" ? a.pitch - b.pitch : b.pitch - a.pitch));
    order.forEach((n, i) => {
      if (i === 0) return;
      const end = n.startTime + n.duration;
      const startTime = r4(Math.min(n.startTime + i * offsetBeats, end - MIN_NOTE_BEATS));
      if (startTime <= n.startTime) return;
      moved.set(n, { ...n, startTime, duration: r4(end - startTime) });
    });
  }
  if (moved.size === 0) return [...notes];
  return notes.map((n) => moved.get(n) ?? n);
}

/**
 * FL's Flam: a grace note `offsetBeats` before each keyed note (all when
 * `keys` is empty), on its pitch, `offsetBeats` long and at `velocityScale`
 * of its velocity — the drummer's double stroke. A note too near the
 * clip's start for one, or already preceded by a note on its pitch that
 * would be overwritten, is left alone.
 */
export function flamNotes(
  notes: readonly WireNote[],
  keys: ReadonlySet<string>,
  offsetBeats: number,
  velocityScale = 0.6,
): WireNote[] {
  if (!(offsetBeats > 0)) return [...notes];
  const all = keys.size === 0;
  const added: WireNote[] = [];
  for (const n of notes) {
    if (!all && !keys.has(noteKey(n))) continue;
    const startTime = r4(n.startTime - offsetBeats);
    if (startTime < -1e-9) continue;
    const taken = notes.some((o) => o.pitch === n.pitch && o !== n && o.startTime < n.startTime - 1e-9 && o.startTime + o.duration > startTime + 1e-9);
    if (taken) continue;
    added.push({ pitch: n.pitch, startTime, duration: r4(offsetBeats), velocity: Math.min(127, Math.max(1, Math.round(n.velocity * velocityScale))) });
  }
  if (added.length === 0) return [...notes];
  return [...notes, ...added];
}

/**
 * Paint (FL's brush): one more note of `length` at `start` on `pitch` —
 * unless a note already starts there on that pitch, in which case the
 * same list comes back (a sweep re-crossing a cell must not stack). The
 * note is trimmed to the clip; a start at or past its end adds nothing.
 */
export function paintNote(
  notes: readonly WireNote[],
  pitch: number,
  start: number,
  length: number,
  clipDuration: number,
  velocity = 100,
): WireNote[] {
  if (start < 0 || start >= clipDuration - 1e-9) return [...notes];
  const p = Math.min(Math.max(0, Math.round(pitch)), 127);
  if (notes.some((n) => n.pitch === p && Math.abs(n.startTime - start) < 1e-9)) return [...notes];
  const note: WireNote = { pitch: p, startTime: r4(start), duration: r4(Math.max(MIN_NOTE_BEATS, Math.min(length, clipDuration - start))), velocity };
  return [...notes, note];
}

// ---- Chord stamp -------------------------------------------------------------

/** The chords the stamp offers: semitones above the root. */
export const CHORDS: ReadonlyArray<{ id: string; label: string; intervals: readonly number[] }> = [
  { id: "major", label: "Major", intervals: [0, 4, 7] },
  { id: "minor", label: "Minor", intervals: [0, 3, 7] },
  { id: "dim", label: "Dim", intervals: [0, 3, 6] },
  { id: "aug", label: "Aug", intervals: [0, 4, 8] },
  { id: "sus2", label: "Sus2", intervals: [0, 2, 7] },
  { id: "sus4", label: "Sus4", intervals: [0, 5, 7] },
  { id: "7", label: "7th", intervals: [0, 4, 7, 10] },
  { id: "m7", label: "Min 7", intervals: [0, 3, 7, 10] },
  { id: "maj7", label: "Maj 7", intervals: [0, 4, 7, 11] },
  { id: "5", label: "Power", intervals: [0, 7] },
  { id: "8", label: "Octave", intervals: [0, 12] },
];

export function chordById(id: string | undefined): (typeof CHORDS)[number] | null {
  return CHORDS.find((c) => c.id === id) ?? null;
}

/**
 * FL's Stamp: the chord's notes on `root`, `length` long at `start`, every
 * one inside 0..127 and the clip, same-pitch neighbours yielding. The
 * root alone when `chordId` names no chord — so a caller can route every
 * draw through here. Hands back what landed, for selection and audition.
 */
export function stampChord(
  notes: readonly WireNote[],
  root: number,
  chordId: string | undefined,
  start: number,
  length: number,
  clipDuration: number,
  velocity = 100,
): { notes: WireNote[]; added: WireNote[] } {
  const chord = chordById(chordId);
  const intervals = chord ? chord.intervals : [0];
  const at = Math.min(Math.max(0, start), Math.max(0, clipDuration - MIN_NOTE_BEATS));
  const duration = r4(Math.max(MIN_NOTE_BEATS, Math.min(length, clipDuration - at)));
  const added: WireNote[] = [];
  for (const i of intervals) {
    const pitch = Math.round(root) + i;
    if (pitch < 0 || pitch > 127) continue;
    added.push({ pitch, startTime: r4(at), duration, velocity });
  }
  if (added.length === 0) return { notes: [...notes], added: [] };
  return { notes: [...resolveSamePitchOverlaps(added, notes), ...added], added };
}

/** Keys of every note on `pitch` — a click on the key column selects them. */
export function notesAtPitch(notes: readonly WireNote[], pitch: number): Set<string> {
  const out = new Set<string>();
  for (const n of notes) if (n.pitch === pitch) out.add(noteKey(n));
  return out;
}
